import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { MdOutlineArrowUpward, MdOutlineArrowDownward, MdOutlineDeleteOutline } from "react-icons/md";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader/Loader";
import SlidePanel from "../../../../components/SlidePanel/SlidePanel";
import * as workflowService from "../../../../services/workflowService";
import FlowPreview from "./FlowPreview";

const labelStyle = { fontSize: "13px", fontWeight: 500 };
const smallBtn = { background: "none", border: "none", padding: "4px", color: "#6B7280", cursor: "pointer", display: "inline-flex" };

let nextId = 1;
const withId = (step) => ({ ...step, _id: nextId++ });
const startDraft = (editing) =>
  editing
    ? { ...editing, steps: workflowService.withDefaultPrimary((editing.steps || []).map(withId)) }
    : { ...workflowService.emptyTemplate(), steps: [withId(workflowService.newPrimaryStep())] };

// A segmented two-way choice (Required | Optional, Then | At the same time).
function Segmented({ value, options, onChange, label, disabled }) {
  return (
    <div className="btn-group btn-group-sm" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className={`btn ${value === o.value ? "btn-danger" : "btn-outline-secondary"}`}
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// One step as a card: what it is called, who does it, whether it is required, and
// whether it follows the previous step. Module scope on purpose - a row defined
// inside the builder would remount (and lose focus) on every keystroke.
function StepRow({ step, index, count, whoOptions, onChange, onMove, onRemove }) {
  const who = workflowService.whoValue(step);
  const removable = workflowService.canRemoveStep(step, count);
  return (
    <div className="mb-2 p-2" style={{ border: "1px solid #E5E7EB", borderRadius: "10px", background: "#fff" }} data-testid="step-row">
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ width: "22px", height: "22px", borderRadius: "50%", background: "#C2185B", color: "#fff", fontSize: "12px", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {index + 1}
        </span>
        <Form.Control
          size="sm"
          value={step.name}
          maxLength={120}
          placeholder="Step name, e.g. Drop-off"
          aria-label={`Step ${index + 1} name`}
          onChange={(e) => onChange({ ...step, name: e.target.value })}
        />
        <button type="button" style={smallBtn} onClick={() => onMove(-1)} disabled={index === 0} aria-label={`Move step ${index + 1} up`}>
          <MdOutlineArrowUpward size={18} />
        </button>
        <button type="button" style={smallBtn} onClick={() => onMove(1)} disabled={index === count - 1} aria-label={`Move step ${index + 1} down`}>
          <MdOutlineArrowDownward size={18} />
        </button>
        <button
          type="button"
          style={{ ...smallBtn, color: removable ? "#C0335A" : "#D1D5DB" }}
          onClick={onRemove}
          disabled={!removable}
          title={step.is_primary ? "The service step can't be removed" : undefined}
          aria-label={`Remove step ${index + 1}`}
        >
          <MdOutlineDeleteOutline size={18} />
        </button>
      </div>
      {step.is_primary ? (
        <div className="mb-2" style={{ fontSize: "12px", color: "#6B7280" }}>
          <strong style={{ color: "#C2185B" }}>★ Service step</strong> - the person who completes the service. It can't be removed, but it can be moved.
        </div>
      ) : null}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <Form.Select
          size="sm"
          style={{ width: "auto", minWidth: "160px" }}
          value={who}
          aria-label={`Step ${index + 1} who`}
          disabled={workflowService.isWhoLocked(step)}
          title={workflowService.isWhoLocked(step) ? "The service step is always done by the service provider" : undefined}
          onChange={(e) => onChange(workflowService.applyWho(step, e.target.value))}
        >
          {whoOptions.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled && o.value !== who}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Segmented
          label={`Step ${index + 1} required`}
          value={step.is_mandatory ? 1 : 0}
          options={[{ value: 1, label: "Required" }, { value: 0, label: "Optional" }]}
          onChange={(v) => onChange({ ...step, is_mandatory: v })}
        />
        {index > 0 && (
          <Segmented
            label={`Step ${index + 1} timing`}
            value={step.mode}
            options={[{ value: "SEQUENTIAL", label: "After the previous" }, { value: "PARALLEL", label: "At the same time" }]}
            onChange={(v) => onChange({ ...step, mode: v })}
          />
        )}
      </div>
      {step.is_primary ? (
        <div className="mt-2" style={{ fontSize: "12px", color: "#6B7280" }} data-testid="service-step-hint">
          {step.is_mandatory
            ? "Required: the service provider is chosen on the booking's crew calendar when booking."
            : "Optional: book a time now and assign the service provider later - from the booking, or the dashboard."}
        </div>
      ) : null}
    </div>
  );
}

// Builds or edits one workflow. `skills` are the vendor's industry's skills (their
// wording, e.g. Coach / Driver); `resourceLabel` is the industry's word for a resource.
export default function WorkflowBuilder({ show, editing, skills, resourceLabel, onHide, onSaved }) {
  const [draft, setDraft] = useState(() => startDraft(null));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setDraft(startDraft(editing));
    setError("");
  }, [show, editing]);

  const steps = draft.steps;
  const hasResourceStep = steps.some((s) => s.kind === "RESOURCE");
  const whoOptions = [
    { value: workflowService.WHO_ANYONE, label: "Anyone" },
    ...skills.map((s) => ({ value: s.skill_key, label: s.label })),
    { value: workflowService.WHO_RESOURCE, label: resourceLabel, disabled: hasResourceStep },
  ];
  // A step whose skill this industry no longer offers still has to show something.
  const optionsFor = (step) => {
    const who = workflowService.whoValue(step);
    return whoOptions.some((o) => o.value === who) ? whoOptions : [...whoOptions, { value: who, label: who }];
  };

  // After any change, a draft with no service provider step gets one if a professional step exists.
  const setStep = (index, next) =>
    setDraft((d) => ({ ...d, steps: workflowService.withDefaultPrimary(d.steps.map((s, i) => (i === index ? next : s))) }));
  const addStep = () => setDraft((d) => ({ ...d, steps: [...d.steps, withId(workflowService.newStep())] }));
  // The service step stays (the button is disabled; this is the same rule for any other caller).
  const removeStep = (index) =>
    setDraft((d) =>
      workflowService.canRemoveStep(d.steps[index] || {}, d.steps.length)
        ? { ...d, steps: workflowService.withDefaultPrimary(d.steps.filter((_, i) => i !== index)) }
        : d
    );
  const moveStep = (index, delta) => setDraft((d) => ({ ...d, steps: workflowService.moveStep(d.steps, index, delta) }));

  const save = async () => {
    const problem = workflowService.validateDraft(draft);
    if (problem) return setError(problem);
    setSaving(true);
    try {
      const payload = workflowService.toPayload(draft);
      const res = editing?.id
        ? await workflowService.updateWorkflowTemplate(editing.id, payload)
        : await workflowService.createWorkflowTemplate(payload);
      if (res?.success) {
        toast.success(editing?.id ? "Workflow saved" : "Workflow created");
        onSaved(res.data?.id ?? editing?.id);
      } else {
        setError(workflowService.resultMessage(res));
      }
    } catch (e) {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title={editing?.id ? "Edit workflow" : "New workflow"}
      width={620}
      footer={
        <>
          <button className="btn btn-outline-secondary px-4" onClick={onHide}>
            Cancel
          </button>
          <button className="btn Button primaryBackground text-white px-4" onClick={save} disabled={saving}>
            {saving ? <Loader style={{ width: 40, height: 20 }} /> : "Save"}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label" style={labelStyle}>
          Name
        </label>
        <Form.Control
          value={draft.name}
          maxLength={120}
          placeholder="e.g. Cleaning with drop-off and pick-up"
          aria-label="Workflow name"
          onChange={(e) => {
            setDraft((d) => ({ ...d, name: e.target.value }));
            setError("");
          }}
        />
      </div>

      <div className="mb-2" style={labelStyle}>
        Steps
      </div>
      <div className="mb-3 p-2" style={{ background: "#F9FAFB", borderRadius: "10px" }}>
        <FlowPreview steps={steps} skills={skills} resourceLabel={resourceLabel} showLegend />
      </div>

      {steps.map((step, i) => (
        <StepRow
          key={step._id}
          step={step}
          index={i}
          count={steps.length}
          whoOptions={optionsFor(step)}
          onChange={(next) => {
            setStep(i, next);
            setError("");
          }}
          onMove={(delta) => moveStep(i, delta)}
          onRemove={() => removeStep(i)}
        />
      ))}
      <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={addStep} disabled={steps.length >= 12}>
        + Add a step
      </button>

      <div className="mb-3">
        <Form.Check
          type="checkbox"
          id="wf-auto-in-progress"
          label="Mark the booking In Progress when the first step starts"
          checked={!!draft.auto_in_progress}
          onChange={(e) => setDraft((d) => ({ ...d, auto_in_progress: e.target.checked ? 1 : 0 }))}
        />
        <Form.Check
          type="checkbox"
          id="wf-auto-complete"
          label="Mark the booking Completed when every required step is done"
          checked={!!draft.auto_complete}
          onChange={(e) => setDraft((d) => ({ ...d, auto_complete: e.target.checked ? 1 : 0 }))}
        />
        <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
          You can always change a booking's status by hand.
        </div>
      </div>

      {error && (
        <div className="text-danger" style={{ fontSize: "13px" }} role="alert">
          {error}
        </div>
      )}
    </SlidePanel>
  );
}
